<app-menu>
  <div class="auth-debug-page">
    <div class="auth-debug-hero">
      <div>
        <div class="auth-debug-eyebrow">Authorization Debug</div>
        <h1>Auth and Permission Test Panel</h1>
        <p>
          This page shows your live session context, decoded token timing, and if you are a super admin,
          lets you inspect route and permission expectations for another user.
        </p>
      </div>

      <div class="auth-debug-actions">
        <mat-form-field appearance="outline" class="auth-debug-selector" *ngIf="canInspectOtherUsers">
          <mat-label>Inspect User</mat-label>
          <mat-select [(ngModel)]="selectedUserId" (selectionChange)="onInspectedUserChange()" [disabled]="loading" panelClass="auth-debug-select-panel">
            <mat-select-trigger>
              <div class="auth-debug-selector-trigger" *ngIf="selectedInspectableUser as selectedUser">
                <div class="auth-debug-selector-trigger__email">{{ selectedUser.email }}</div>
                <div class="auth-debug-selector-trigger__meta">
                  {{ formatInspectableUserMeta(selectedUser) }}
                  <span class="auth-debug-selector-trigger__roles"> · {{ formatInspectableUserRoles(selectedUser, 2) }}</span>
                </div>
              </div>
            </mat-select-trigger>
            <mat-option *ngFor="let user of inspectableUsers; trackBy: trackByInspectableUserId" [value]="user.user_id">
              <div class="auth-debug-select-option">
                <div class="auth-debug-select-option__email">{{ user.email }}</div>
                <div class="auth-debug-select-option__meta">{{ formatInspectableUserMeta(user) }}</div>
                <div class="auth-debug-select-option__roles">{{ formatInspectableUserRoles(user, 4) }}</div>
              </div>
            </mat-option>
          </mat-select>
        </mat-form-field>
        <button class="auth-debug-button auth-debug-button--primary" (click)="refreshDebugData()" [disabled]="loading">
          {{ loading ? 'Running Checks...' : 'Refresh Checks' }}
        </button>
        <div class="auth-debug-updated" *ngIf="lastUpdated">Last updated: {{ lastUpdated }}</div>
      </div>
    </div>

    <div class="auth-debug-banner auth-debug-banner--error" *ngIf="errorMessage">
      {{ errorMessage }}
    </div>

    <div class="auth-debug-grid">
      <section class="auth-debug-card">
        <div class="auth-debug-card__title">Current Session User</div>
        <div class="auth-debug-list" *ngIf="sessionUser; else noSessionUser">
          <div><strong>Email:</strong> {{ sessionUser.email }}</div>
          <div><strong>User ID:</strong> {{ sessionUser.user_id }}</div>
          <div><strong>Roles Mask:</strong> {{ sessionUser.roles_mask }}</div>
          <div><strong>Roles:</strong> {{ sessionUser.roles.join(', ') || 'None' }}</div>
          <div><strong>Permissions:</strong> {{ sessionUser.permissions.join(', ') || 'None' }}</div>
        </div>

        <ng-template #noSessionUser>
          <div class="auth-debug-empty">No current session user context loaded.</div>
        </ng-template>
      </section>

      <section class="auth-debug-card">
        <div class="auth-debug-card__title">Inspected User</div>
        <div class="auth-debug-list" *ngIf="inspectedUser; else noInspectedUser">
          <div><strong>Email:</strong> {{ inspectedUser.email }}</div>
          <div><strong>User ID:</strong> {{ inspectedUser.user_id }}</div>
          <div><strong>Roles Mask:</strong> {{ inspectedUser.roles_mask }}</div>
          <div><strong>Roles:</strong> {{ inspectedUser.roles.join(', ') || 'None' }}</div>
          <div><strong>Permissions:</strong> {{ inspectedUser.permissions.join(', ') || 'None' }}</div>
        </div>

        <ng-template #noInspectedUser>
          <div class="auth-debug-empty">No inspected user context loaded.</div>
        </ng-template>
      </section>

      <section class="auth-debug-card auth-debug-card--wide">
        <div class="auth-debug-card__title">Token Summary</div>

        <div class="token-grid">
          <div class="token-card">
            <div class="token-card__title">{{ accessTokenSummary.label }}</div>
            <div><strong>Present:</strong> {{ accessTokenSummary.present ? 'Yes' : 'No' }}</div>
            <div><strong>User ID:</strong> {{ accessTokenSummary.userId ?? 'n/a' }}</div>
            <div><strong>Issued At:</strong> {{ accessTokenSummary.issuedAt ?? 'n/a' }}</div>
            <div><strong>Expires At:</strong> {{ accessTokenSummary.expiresAt ?? 'n/a' }}</div>
            <div><strong>Seconds Left:</strong> {{ accessTokenSummary.secondsUntilExpiry ?? 'n/a' }}</div>
            <div class="token-card__error" *ngIf="accessTokenSummary.error">{{ accessTokenSummary.error }}</div>
          </div>

          <div class="token-card">
            <div class="token-card__title">{{ refreshTokenSummary.label }}</div>
            <div><strong>Present:</strong> {{ refreshTokenSummary.present ? 'Yes' : 'No' }}</div>
            <div><strong>User ID:</strong> {{ refreshTokenSummary.userId ?? 'n/a' }}</div>
            <div><strong>Issued At:</strong> {{ refreshTokenSummary.issuedAt ?? 'n/a' }}</div>
            <div><strong>Expires At:</strong> {{ refreshTokenSummary.expiresAt ?? 'n/a' }}</div>
            <div><strong>Seconds Left:</strong> {{ refreshTokenSummary.secondsUntilExpiry ?? 'n/a' }}</div>
            <div class="token-card__error" *ngIf="refreshTokenSummary.error">{{ refreshTokenSummary.error }}</div>
          </div>
        </div>
      </section>
    </div>

    <div class="auth-debug-grid auth-debug-grid--stacked">
      <section class="auth-debug-card">
        <div class="auth-debug-card__title">Route Expectations</div>
        <div class="auth-debug-card__subtitle">
          These are calculated from the inspected user context.
        </div>

        <div class="probe-table">
          <div class="probe-table__row probe-table__row--head">
            <div>Route</div>
            <div>Required Permissions</div>
            <div>Should Pass</div>
          </div>

          <div class="probe-table__row" *ngFor="let probe of routeProbes; trackBy: trackByPath">
            <div>
              <a [routerLink]="[probe.path]">{{ probe.path }}</a>
            </div>
            <div>{{ probe.permissions.join(', ') || 'authenticated only' }}</div>
            <div>
              <span class="probe-pill" [class.probe-pill--ok]="probe.allowed" [class.probe-pill--bad]="!probe.allowed">
                {{ probe.allowed ? 'Allowed' : 'Blocked' }}
              </span>
            </div>
          </div>
        </div>
      </section>

      <section class="auth-debug-card">
        <div class="auth-debug-card__title">API Expectations</div>
        <div class="auth-debug-card__subtitle">
          These are calculated from the inspected user context, not by impersonating that user.
        </div>

        <div class="probe-table">
          <div class="probe-table__row probe-table__row--head">
            <div>API</div>
            <div>Required Permissions</div>
            <div>Should Pass</div>
            <div>Notes</div>
          </div>

          <div class="probe-table__row probe-table__row--api" *ngFor="let probe of apiExpectations; trackBy: trackByUrl">
            <div>
              <div class="probe-label">{{ probe.label }}</div>
              <div class="probe-url">{{ probe.url }}</div>
            </div>
            <div>{{ probe.permissions.join(', ') || 'authenticated only' }}</div>
            <div>
              <span class="probe-pill" [class.probe-pill--ok]="probe.allowed" [class.probe-pill--bad]="!probe.allowed">
                {{ probe.allowed ? 'Allowed' : 'Blocked' }}
              </span>
            </div>
            <div class="probe-subtle">
              Expected from the selected user&rsquo;s roles and permissions.
            </div>
          </div>
        </div>
      </section>

      <section class="auth-debug-card">
        <div class="auth-debug-card__title">Live API Probes</div>
        <div class="auth-debug-card__subtitle">
          These requests still execute as your current logged-in session.
        </div>

        <div class="probe-table">
          <div class="probe-table__row probe-table__row--head">
            <div>API</div>
            <div>Expected</div>
            <div>Status</div>
            <div>Response Preview</div>
          </div>

          <div class="probe-table__row probe-table__row--api" *ngFor="let probe of liveApiProbeResults; trackBy: trackByUrl">
            <div>
              <div class="probe-label">{{ probe.label }}</div>
              <div class="probe-url">{{ probe.url }}</div>
            </div>
            <div>
              <div>{{ probe.expectedAllowed ? 'Should pass' : 'Should fail' }}</div>
              <div class="probe-subtle">{{ probe.permissions.join(', ') || 'authenticated only' }}</div>
            </div>
            <div>
              <span class="probe-pill" [class.probe-pill--ok]="probe.ok" [class.probe-pill--bad]="!probe.ok">
                {{ probe.status ?? 'n/a' }}
              </span>
            </div>
            <div>
              <pre>{{ probe.bodyPreview || probe.error || 'No response body' }}</pre>
            </div>
          </div>
        </div>
      </section>

      <section class="auth-debug-card">
        <div class="auth-debug-card__title">Raw Inspected /api/me.php Response</div>
        <pre>{{ inspectedUserJson || 'No payload loaded.' }}</pre>
      </section>
    </div>
  </div>
</app-menu>
